import { DueDiligenceQuestionType } from 'Models/DueDiligence';
import { IrqQuestionType } from 'Models/InherentRisk';

import { RequiresAttentionMarker } from './RequiresAttentionMarker';

interface UnansweredQuestionMarkerProps {
    questionIsRequired: boolean;
    questionType: IrqQuestionType | DueDiligenceQuestionType;
}

/**
 * Given a question type belonging to an IRQ or DDQ, renders text indicating that the question is unanswered, and what is required for it to be considered answered.
 * Ideally, we consolidate types into a single QuestionType in the future.
 * Because this component renderes a relatively long string, it should be used alongside the question text and answer input, not in other areas (such as the sidebar of a page).
 */
export const UnansweredQuestionMarker = (props: UnansweredQuestionMarkerProps) => {
    return (
        <RequiresAttentionMarker type={props.questionIsRequired ? 'negative' : 'warning'} size="sm">
            {describeUnansweredQuestion(props.questionIsRequired, props.questionType)}
        </RequiresAttentionMarker>
    );
};

const describeUnansweredQuestion = (questionIsRequired: boolean, questionType: IrqQuestionType | DueDiligenceQuestionType): string => {
    const questionNoun = questionIsRequired ? 'Required question' : 'Question';

    switch (questionType) {
        case DueDiligenceQuestionType.DATE:
        case IrqQuestionType.DATE:
            return `${questionNoun} not answered (to answer, select a date)`;
        case DueDiligenceQuestionType.FILE_UPLOAD:
        case IrqQuestionType.FILE_UPLOAD:
            return `${questionNoun} not answered (to answer, upload one or more files)`;
        case DueDiligenceQuestionType.FREEFORM_TEXT:
        case IrqQuestionType.FREEFORM_TEXT:
            return `${questionNoun} not answered (to answer, add text)`;
        case DueDiligenceQuestionType.MULTIPLE_SELECT:
        case IrqQuestionType.MULTIPLE_SELECT:
            return `${questionNoun} not answered (to answer, select one or more options)`;
        case DueDiligenceQuestionType.SINGLE_SELECT:
        case IrqQuestionType.SINGLE_SELECT:
            return `${questionNoun} not answered (to answer, select an option)`;
    }
};
